import React, { useState, useCallback, useMemo, useEffect } from "react";
import CheckIcon from "../../../assets/svg/check-icon";
import SearchIcon from "../../../assets/svg/search-icon";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useGetCampaignsQuery,
  useLazyGetCampaignsQuery,
} from "../../../redux/services/campaignApi";
import {
  setCommunicationsCampaign,
  setCampaignCommunicationsCampaign,
  setCampaignLogsCampaign,
  selectCampaignCommunicationsFilters,
  FilterState,
  selectCampaignLogsFilters,
  selectCommunicationsFilters,
  clearCampaignCommunicationsMode,
  clearCampaignCommunicationsStatus,
  clearCampaignCommunicationsOutcome,
} from "../../../redux/slices/filterSlice";
import { transformCampaign } from "../../../pages/Communications/utils/constants";
import { SearchableDropdownOption } from "../../SearchableDropdown/types";
import { Campaign } from "../../../pages/Campaigns/utils/types";
import { WORKFLO_NAME } from "../../../utils/constants/globalConstants";

const SelectCampaignFilter = ({
  onClose,
  isCampaignCommunication = false,
  isCampaignLogs = false,
}: {
  onClose: () => void;
  isCampaignCommunication?: boolean;
  isCampaignLogs?: boolean;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedCampaignState = useSelector(
    isCampaignCommunication
      ? (state: { filter: FilterState }) =>
          selectCampaignCommunicationsFilters(state)?.campaign
      : isCampaignLogs
      ? (state: { filter: FilterState }) =>
          selectCampaignLogsFilters(state)?.campaign
      : (state: { filter: FilterState }) =>
          selectCommunicationsFilters(state)?.campaign
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<
    SearchableDropdownOption[]
  >([]);
  const [isSearching, setIsSearching] = useState(false);

  // Get initial campaigns data
  const {
    data: campaigns,
    isLoading: isLoadingCampaigns,
    isFetching: isFetchingCampaigns,
  } = useGetCampaignsQuery({
    search_query: "",
    skip: 0,
    limit: 100,
  });

  // For search functionality
  const [searchCampaigns] = useLazyGetCampaignsQuery();

  const transformedCampaignsInitialOptions = useMemo(() => {
    if (!campaigns?.campaigns) return [];
    const transformedCampaigns = campaigns?.campaigns
      ?.filter((campaign: Campaign) => campaign.status !== "draft")
      .map(transformCampaign);
    return transformedCampaigns;
  }, [campaigns]);

  const handleCampaignSearch = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        const response = await searchCampaigns({
          search_query: query,
          skip: 0,
          limit: 50,
        }).unwrap();

        if (response?.campaigns) {
          const transformed = response.campaigns.map(transformCampaign);
          setSearchResults(transformed);
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        console.error("Campaign search failed:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [searchCampaigns]
  );

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleCampaignSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, handleCampaignSearch]);

  const displayedCampaigns = searchQuery.trim()
    ? searchResults
    : transformedCampaignsInitialOptions;

  const handleCampaignSelect = useCallback(
    (campaignId: string) => {
      const campaign = displayedCampaigns.find(
        (c: SearchableDropdownOption) => c.value === campaignId
      );

      if (campaignId === selectedCampaignState.value) {
        if (isCampaignCommunication) {
          dispatch(
            setCommunicationsCampaign({
              value: "",
              displayValue: `Select ${WORKFLO_NAME.singular}`,
            })
          );
          dispatch(clearCampaignCommunicationsMode());
          dispatch(clearCampaignCommunicationsStatus());
          dispatch(clearCampaignCommunicationsOutcome());
          // Update URL to remove campaign ID
          navigate("/dashboard/campaigns/manage/all");
        } else if (isCampaignLogs) {
          dispatch(
            setCampaignLogsCampaign({
              value: "",
              displayValue: `Select ${WORKFLO_NAME.singular}`,
            })
          );
        } else {
          dispatch(
            setCommunicationsCampaign({
              value: "",
              displayValue: `Select ${WORKFLO_NAME.singular}`,
            })
          );
        }
      } else if (campaign) {
        if (isCampaignCommunication) {
          dispatch(clearCampaignCommunicationsMode());
          dispatch(clearCampaignCommunicationsStatus());
          dispatch(clearCampaignCommunicationsOutcome());
          dispatch(
            setCampaignCommunicationsCampaign({
              value: campaign.value,
              displayValue: campaign.label,
            })
          );
          // Update URL with new campaign ID
          // navigate(`/dashboard/campaigns/${campaign.value}/communications`);
        } else if (isCampaignLogs) {
          dispatch(
            setCampaignLogsCampaign({
              value: campaign.value,
              displayValue: campaign.label,
            })
          );
        } else {
          dispatch(
            setCommunicationsCampaign({
              value: campaign.value,
              displayValue: campaign.label,
            })
          );
        }
      }
      onClose();
    },
    [
      dispatch,
      selectedCampaignState.value,
      displayedCampaigns,
      navigate,
      isCampaignCommunication,
      onClose,
      isCampaignLogs,
    ]
  );

  const handleCampaignClear = () => {
    if (isCampaignCommunication) {
      dispatch(
        setCampaignCommunicationsCampaign({
          value: "",
          displayValue: `Select ${WORKFLO_NAME.singular}`,
        })
      );
      // Update URL to remove campaign ID
      navigate("/dashboard/campaigns/manage/all");
    } else if (isCampaignLogs) {
      dispatch(
        setCampaignLogsCampaign({
          value: "",
          displayValue: `Select ${WORKFLO_NAME.singular}`,
        })
      );
    } else {
      dispatch(
        setCommunicationsCampaign({
          value: "",
          displayValue: `Select ${WORKFLO_NAME.singular}`,
        })
      );
    }

    onClose();
  };

  return (
    <div className="flex flex-col">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={`Search ${WORKFLO_NAME.singular}`}
          className="w-full pl-10 pr-4 py-2 text-sm outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
        />
      </div>

      {/* Selected Campaign Display */}
      {selectedCampaignState.value && (
        <div className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk">
          <div className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
            <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
              {selectedCampaignState.displayValue}
            </span>
          </div>
          <button
            onClick={handleCampaignClear}
            className="text-xs text-verba-blue dark:text-verba-blue-dk hover:underline"
          >
            Clear
          </button>
        </div>
      )}

      {/* Campaigns List */}
      <div className="flex flex-col">
        <div className="px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk">
          All {WORKFLO_NAME.plural}
        </div>

        <div className="overflow-y-auto">
          {isSearching || isLoadingCampaigns || isFetchingCampaigns ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {isLoadingCampaigns
                  ? `Loading ${WORKFLO_NAME.plural}...`
                  : isFetchingCampaigns
                  ? `Fetching ${WORKFLO_NAME.plural}...`
                  : `Searching ${WORKFLO_NAME.plural}...`}
              </div>
            </div>
          ) : displayedCampaigns.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {searchQuery.trim()
                  ? `No ${WORKFLO_NAME.plural} found`
                  : `No ${WORKFLO_NAME.plural} available`}
              </div>
            </div>
          ) : (
            displayedCampaigns.map((campaign: SearchableDropdownOption) => (
              <div
                key={campaign.value}
                onClick={() => handleCampaignSelect(campaign.value)}
                className={`flex items-center justify-between p-3 cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk border-b border-border-gray dark:border-border-gray-dk last:border-b-0 ${
                  selectedCampaignState.value === campaign.value
                    ? "bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk"
                    : ""
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-heading-text dark:text-heading-text-dk truncate">
                    {campaign.content}
                  </div>
                </div>
                {selectedCampaignState.value === campaign.value && (
                  <CheckIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk shrink-0 ml-2" />
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Action Buttons */}
      {/* <div className="flex justify-end gap-2 mt-4 pb-2">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
        />
        <Button
          innerText="Apply"
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div> */}
    </div>
  );
};

export default SelectCampaignFilter;
